import {
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { CRUMB_SLOTS } from '../lib/breadcrumbTypes';
import {
  BreadcrumbReadContext,
  BreadcrumbWriteContext,
  type BreadcrumbEntry,
} from './breadcrumbContextValues';
import { BreadcrumbFocusProvider } from './BreadcrumbFocusContext';

export function BreadcrumbProvider({
  caseKey,
  children,
}: {
  caseKey: string;
  children: ReactNode;
}) {
  const { pathname, search } = useLocation();
  const scope = JSON.stringify([caseKey, pathname, search]);
  const [entries, setEntries] = useState<BreadcrumbEntry[]>([]);

  const put = useCallback((entry: BreadcrumbEntry) => {
    setEntries(previous => {
      const existing = previous.find(
        candidate => candidate.owner === entry.owner && candidate.scope === entry.scope,
      );
      if (
        existing &&
        existing.contribution.slot === entry.contribution.slot &&
        existing.contribution.label === entry.contribution.label &&
        existing.contribution.path === entry.contribution.path
      ) {
        return previous;
      }

      return [...previous.filter(candidate => candidate.owner !== entry.owner), entry];
    });
  }, []);

  const remove = useCallback((owner: symbol, entryScope: string) => {
    setEntries(previous =>
      previous.some(candidate => candidate.owner === owner && candidate.scope === entryScope)
        ? previous.filter(
            candidate => !(candidate.owner === owner && candidate.scope === entryScope),
          )
        : previous,
    );
  }, []);

  const writer = useMemo(() => ({ scope, put, remove }), [scope, put, remove]);
  const contributions = useMemo(
    () =>
      CRUMB_SLOTS.flatMap(slot => {
        const matches = entries.filter(
          entry => entry.scope === scope && entry.contribution.slot === slot,
        );
        return matches.length === 1 ? [matches[0].contribution] : [];
      }),
    [entries, scope],
  );

  return (
    <BreadcrumbFocusProvider>
      <BreadcrumbWriteContext.Provider value={writer}>
        <BreadcrumbReadContext.Provider value={contributions}>
          {children}
        </BreadcrumbReadContext.Provider>
      </BreadcrumbWriteContext.Provider>
    </BreadcrumbFocusProvider>
  );
}
